import type { Node as PMNode } from '@tiptap/pm/model'
import { parsePipeTable, type GridSnapshot } from '../../lib/grid'

/**
 * The one-way escape hatch: a tiptap table that has outgrown prose becomes its own GRID PART.
 *
 * WHY ONE-WAY. A table inside prose is illustrative — it has to flow inside the argument — while a
 * grid part IS the deliverable. The real migration people hit runs in this direction only: a
 * pipe-table that has grown past what a paragraph can carry. The reverse already exists as the
 * backend's lossy `gridToMarkdown` projection, and re-importing it would mean deciding what to do
 * with a multi-line cell — the very thing a pipe-table cannot express.
 *
 * The node is rendered to a GFM pipe-table and handed to `lib/grid.ts#parsePipeTable`, rather than
 * walked into a GridSnapshot directly, so there is exactly ONE place that decides how a table's
 * columns are typed — the same code path a pasted markdown table would take.
 */

/** A tiptap `table` node as a GFM pipe-table, `|` escaped and newlines collapsed. */
export function tableToPipeTable(table: PMNode): string {
  const rows: string[][] = []
  table.forEach((row) => {
    const cells: string[] = []
    row.forEach((cell) => {
      cells.push(cell.textContent.replace(/\r\n?|\n/g, ' ').replace(/\|/g, '\\|').trim())
    })
    if (cells.length > 0) rows.push(cells)
  })
  if (rows.length === 0) return ''
  const width = Math.max(...rows.map((r) => r.length))
  const pad = (cells: string[]): string =>
    `| ${Array.from({ length: width }, (_, i) => cells[i] ?? '').join(' | ')} |`
  const [header, ...body] = rows
  return [pad(header!), `| ${Array.from({ length: width }, () => '---').join(' | ')} |`, ...body.map(pad)].join('\n')
}

/** The `table` node the selection sits in, or null. */
export function tableAt(doc: PMNode, pos: number): { node: PMNode; from: number; to: number } | null {
  const resolved = doc.resolve(Math.min(Math.max(pos, 0), doc.content.size))
  for (let depth = resolved.depth; depth > 0; depth -= 1) {
    const node = resolved.node(depth)
    if (node.type.name === 'table') {
      const from = resolved.before(depth)
      return { node, from, to: from + node.nodeSize }
    }
  }
  return null
}

/** The grid a table node converts into, or null when it is not readable as one. */
export function tableToGrid(table: PMNode): GridSnapshot | null {
  const markdown = tableToPipeTable(table)
  return markdown ? parsePipeTable(markdown) : null
}
